import React, { useEffect, useState, useCallback } from 'react';
import { Service } from './Service';
import { Row, Col, Button, Input, Label, Card, CardHeader, CardBody, Badge, Spinner } from 'reactstrap';

const Ingredients = () => {
    const service = React.useMemo(() => new Service(), []);
    const [ingredients, setIngredients] = useState([]);
    const [ingredient, setIngredient] = useState(null);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const loadIngredients = useCallback(async () => {
        try {
            setLoading(true);
            const data = await service.getIngredients();
            setIngredients(data || []);
            if (data && data.length > 0) {
                setIngredient(prev => {
                    if (prev && prev.id) {
                        return data.find(i => i.id === prev.id) || data[0];
                    }
                    return data[0];
                });
            }
        } catch (error) {
            console.error('Error loading ingredients:', error);
        } finally {
            setLoading(false);
        }
    }, [service]);

    useEffect(() => {
        loadIngredients();
    }, [loadIngredients]);

    const addIngredient = async () => {
        try {
            setLoading(true);
            const ingr = {
                code: `ING-${Date.now().toString().slice(-4)}`,
                title: 'Νέο Συστατικό',
            };

            const newIngredient = await service.addIngredient(ingr);
            setIngredients([newIngredient, ...ingredients]);
            setIngredient(newIngredient);
        } catch (error) {
            console.error('Error adding ingredient:', error);
        } finally {
            setLoading(false);
        }
    };

    const saveIngredient = async () => {
        if (!ingredient) return;
        try {
            setLoading(true);
            const updatedIngredient = await service.updateIngredient(ingredient);
            setIngredients(ingredients.map(i => i.id === ingredient.id ? updatedIngredient : i));
            setIngredient(updatedIngredient);
        } catch (error) {
            console.error('Error saving ingredient:', error);
        } finally {
            setLoading(false);
        }
    };

    const deleteIngredient = async (id) => {
        if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το συστατικό;')) return;
        try {
            setLoading(true);
            const success = await service.deleteIngredient(id);
            if (success) {
                const nextIngredients = ingredients.filter(i => i.id !== id);
                setIngredients(nextIngredients);
                setIngredient(nextIngredients.length > 0 ? nextIngredients[0] : null);
            }
        } catch (error) {
            console.error('Error deleting ingredient:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredIngredients = ingredients.filter(i => 
        !search || 
        ((i.title && i.title.toLowerCase().includes(search.toLowerCase())) || 
         (i.code && i.code.toLowerCase().includes(search.toLowerCase())))
    );

    return (
        <Row className="g-4">
            <Col lg="4" md="5">
                <div className="ek-card mb-0">
                    <div className="ek-card-header">
                        <h5>
                            <i className="bi bi-pie-chart-fill text-primary"></i> Συστατικά
                        </h5>
                        <Badge color="info" pill>{ingredients.length}</Badge>
                    </div>
                    <div className="ek-card-body">
                        <Button onClick={addIngredient} block color="primary" className="mb-3">
                            <i className="bi bi-plus-lg"></i> Προσθήκη Συστατικού
                        </Button>

                        <div className="ek-search-box">
                            <i className="bi bi-search"></i>
                            <Input
                                type="search"
                                className="ek-search-input"
                                placeholder="Αναζήτηση συστατικού..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        <div className="scrollable-list">
                            {loading && ingredients.length === 0 ? (
                                <div className="text-center py-4">
                                    <Spinner size="sm" color="primary" />
                                    <span className="ms-2 text-muted">Φόρτωση...</span>
                                </div>
                            ) : filteredIngredients.length === 0 ? (
                                <div className="ek-empty-state py-4">
                                    <i className="bi bi-inbox fs-2"></i>
                                    <p className="small text-muted mb-0">Δεν βρέθηκαν συστατικά</p>
                                </div>
                            ) : (
                                <div className="ek-list-group">
                                    {filteredIngredients.map(item => (
                                        <div
                                            key={item.id}
                                            className={`ek-list-item ${ingredient && ingredient.id === item.id ? 'active' : ''}`}
                                            onClick={() => setIngredient(item)}
                                        >
                                            <div>
                                                <div className="ek-list-item-title">{item.title || 'Χωρίς Τίτλο'}</div>
                                                <div className="ek-list-item-sub">
                                                    <i className="bi bi-upc-scan"></i>
                                                    <code>{item.code || 'Χωρίς Κωδικό'}</code>
                                                </div>
                                            </div>
                                            <i className="bi bi-chevron-right text-muted" style={{ fontSize: '0.8rem' }}></i>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Col>

            <Col lg="8" md="7">
                {ingredient ? (
                    <Card className="ek-card">
                        <CardHeader className="ek-card-header">
                            <h5>
                                <i className="bi bi-pencil-square text-primary"></i> 
                                {ingredient.title || 'Επεξεργασία Συστατικού'}
                            </h5>
                            {ingredient.code && <Badge className="badge-brand">{ingredient.code}</Badge>}
                        </CardHeader>
                        <CardBody className="ek-card-body">
                            <div className="ek-form-group mb-3">
                                <Label className="ek-label">
                                    <i className="bi bi-upc"></i> Κωδικός Συστατικού
                                </Label>
                                <Input
                                    value={ingredient.code || ''}
                                    placeholder="π.χ. ING-001"
                                    onChange={(e) => setIngredient({ ...ingredient, code: e.target.value })}
                                />
                            </div>

                            <div className="ek-form-group mb-4">
                                <Label className="ek-label">
                                    <i className="bi bi-tag"></i> Τίτλος Συστατικού
                                </Label>
                                <Input
                                    value={ingredient.title || ''}
                                    placeholder="π.χ. Αλεύρι Τύπου 70%"
                                    onChange={(e) => setIngredient({ ...ingredient, title: e.target.value })}
                                />
                            </div>

                            <div className="d-flex gap-2 pt-2 border-top">
                                <Button onClick={saveIngredient} color="primary" disabled={loading}>
                                    <i className="bi bi-check2-circle"></i> Αποθήκευση
                                </Button>
                                <Button onClick={() => deleteIngredient(ingredient.id)} color="danger" disabled={loading}>
                                    <i className="bi bi-trash3"></i> Διαγραφή
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                ) : (
                    <div className="ek-card">
                        <div className="ek-empty-state">
                            <i className="bi bi-pie-chart"></i>
                            <h4>Επιλέξτε ένα συστατικό</h4>
                            <p>Επιλέξτε ένα συστατικό από την αριστερή λίστα για επεξεργασία ή πατήστε "Προσθήκη Συστατικού" για νέο.</p>
                        </div>
                    </div>
                )}
            </Col>
        </Row>
    );
};

export { Ingredients };
export default Ingredients;
